<template>
  <v-sheet
    elevation="2"
    class="px-12 py-8 rounded white"
    id="profileSettings-personalInformation"
    data-tourid="profileSettings-sheet-personalInformation"
  >
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('profile_settings.personal_information') }}
      </p>
    </div>
    <v-form v-model="formOptions.valid" lazyValidation ref="profile">
      <v-row>
        <v-col>
          <v-text-field
            required
            outlined
            dense
            data-private
            data-testid="profile-textField-displayname"
            v-model="form.displayname"
            :disabled="isLoading"
            :rules="[
              name =>
                !!name.trim() || $t('validation.form.this_field_is_required'),
            ]"
            placeholder="Christopher"
            :label="`${$t('common.users.first_name')}*`"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            required
            outlined
            dense
            data-private
            data-testid="profile-textField-familyname"
            v-model="form.familyname"
            placeholder="Lean"
            :disabled="isLoading"
            :rules="[
              familyname =>
                !!familyname.trim() ||
                $t('validation.form.this_field_is_required'),
            ]"
            :label="`${$t('common.users.last_name')}*`"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            required
            outlined
            dense
            readonly
            data-private
            data-testid="profile-textField-email"
            v-model="form.email"
            placeholder="chrislean@reverscore.com"
            :label="`${$t('common.email_one')}*`"
          >
            <template v-slot:append>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-lock
                  </v-icon>
                </template>
                {{ $t('profile_settings.email_tooltip') }}
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          ><v-text-field
            outlined
            dense
            data-private
            data-testid="profile-textField-nickname"
            v-model="form.nickname"
            placeholder="Chris"
            :disabled="isLoading"
            :label="$t('profile_settings.nickname')"
          ></v-text-field
        ></v-col>
        <v-col>
          <v-text-field
            outlined
            dense
            data-private
            data-testid="profile-textField-employeeId"
            v-model="form.employeeId"
            placeholder="153682"
            :disabled="isLoading"
            :label="$t('profile_settings.employee_id')"
          ></v-text-field
        ></v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            required
            outlined
            dense
            data-private
            data-testid="profile-textField-position"
            v-model="form.position"
            :rules="[
              name =>
                !!name.trim() || $t('validation.form.this_field_is_required'),
            ]"
            :maxlength="$constants.profile.positionMaxLength"
            :placeholder="$t('profile.position_placeholder')"
            :disabled="isLoading"
            :label="$t('profile_settings.job_position')"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="form.timezone"
            dense
            outlined
            required
            :hide-details="Boolean(form.timezone)"
            :label="`${$t('site_settings.timezone')}`"
            :items="timezoneOptions"
          />
        </v-col>
      </v-row>

      <p class="h6 pa-0 mx-0 mb-0 mt-4">{{ $t('common.location') }}</p>
      <LocationSelector
        key="profile-user-location"
        :organizationId="organizationId"
        :location="initialLocation"
        @change="locationSelectorChange"
      />
    </v-form>
    <div class="mt-6 text-right">
      <Button
        size="large"
        color="grey"
        data-testid="profile-button-reset"
        class="me-2"
        outlined
        :disabled="isLoading || !accountChanged"
        :text="$t('common.button.discard_changes')"
        @click="discardChanges"
      />
      <Button
        size="large"
        color="primary"
        data-testid="profile-button-save"
        :loading="isLoading"
        :disabled="shouldDisableSaveButton"
        :text="$t('common.button.save')"
        @click="$emit('update', form)"
      />
    </div>
    <v-dialog
      data-testid="profileSettings-dialog-confirmLeave"
      v-model="showDialog"
      max-width="315"
    >
      <v-card>
        <v-card-title class="pb-0">
          <span class="h6 grey--text text--darken-4">{{
            $t('before_leave_dialog.dialog_header')
          }}</span></v-card-title
        >
        <v-card-text class="pb-3">
          <span class="grey--text text--darken-4">
            {{ $t('before_leave_dialog.dialog_subtitle') }}</span
          >
        </v-card-text>
        <v-card-actions class="pb-3 justify-end">
          <Button
            plain
            data-testid="accountSettingsDialog-button-cancel"
            class="me-6"
            color="grey"
            :text="$t('common.button.cancel')"
            @click="$emit('cancelLeaveRoute')"
          />
          <Button
            plain
            class="me-5"
            data-testid="accountSettingsDialog-button-save"
            :text="$t('common.button.save')"
            color="primary"
            @click="$emit('update', form)"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>
<script>
import _ from 'lodash'
import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import Button from '@/components/UI/Button'
import LocationSelector from '@/components/LocationSelector/LocationSelector.vue'

export default {
  name: 'Profile',

  mixins: [mixin],

  components: {
    Button,
    LocationSelector,
  },

  props: {
    showDialog: {
      type: Boolean,
      required: false,
    },
    account: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },

  watch: {
    account: {
      deep: true,
      handler() {
        this.originalAccount = Object.freeze({
          ...this.account,
          site: this.getLocationLevelId(this.account.site),
          tags: this.getTagIds(this.account?.tags ?? []),
        })
        this.form = { ...this.originalAccount }
      },
    },

    accountChanged(newValue) {
      this.$emit('accountChanged', newValue)
    },
  },

  async created() {
    this.timezoneOptions = await utils.dates.humanizeTimezones()
  },

  data() {
    const originalAccount = Object.freeze({
      ...this.account,
      site: this.getLocationLevelId(this.account.site),
      tags: this.getTagIds(this.account?.tags ?? []),
    })

    return {
      formOptions: {
        valid: true,
      },
      form: { ...originalAccount },
      originalAccount,
      initialLocation: {
        organization: this.account?.organization._id,
        site: originalAccount?.site,
        tags: originalAccount?.tags ?? [],
      },
      timezoneOptions: [],
    }
  },

  computed: {
    accountChanged() {
      return !_.isEqual(this.form, this.originalAccount)
    },

    organizationId() {
      return this.account?.organization?._id
    },

    shouldDisableSaveButton() {
      if (!this.accountChanged) return true
      return (
        this.isLoading || !this.formOptions.valid || !this.hasCompleteAttributes
      )
    },

    hasCompleteAttributes() {
      return Boolean(this.form?.site)
    },
  },

  methods: {
    locationSelectorChange(data) {
      this.form = {
        ...this.form,
        site: data.site,
        tags: data.tags,
      }
    },

    getLocationLevelId(locationLevel) {
      if (_.isString(locationLevel)) return locationLevel
      return locationLevel?._id
    },

    getTagIds(tags) {
      return tags.map(tag => (tag._id ? tag._id : tag))
    },

    discardChanges() {
      this.form = { ...this.originalAccount }
    },
  },
}
</script>
